/**
 * Groupes de rôles porteurs d'une capacité métier.
 *
 * POURQUOI CE MODULE EXISTE
 *   Le 10 août 2026, KHRIBACH Salaheddine (apprenti_be) ne pouvait pas signer
 *   sa feuille d'heures : l'application lui demandait de ventiler ses heures
 *   parce qu'elle le considère comme un BE, mais les policies RLS de
 *   be_ventilations n'autorisaient que {admin, bureau_etude}.
 *
 *   La cause n'était pas l'oubli d'un rôle dans une liste : c'était qu'il
 *   existait plusieurs listes. « Cette personne est-elle BE ? » était répondu
 *   séparément dans Timesheet.tsx, dans son fichier de test, et dans 27
 *   policies SQL. Trois correctifs successifs avaient déjà réparé trois
 *   occurrences sans jamais traiter la dispersion elle-même.
 *
 *   Toute réponse à « ce rôle appartient-il au groupe X ? » passe désormais
 *   par ici. Un groupe défini à un seul endroit ne peut plus diverger d'avec
 *   lui-même.
 *
 * CE QUI N'A PAS ENCORE ÉTÉ RAPATRIÉ
 *   STRONG_PASSWORD_ROLES est aujourd'hui déclaré trois fois — Login.tsx,
 *   AdminEmployees.tsx et l'edge function manage-auth. C'est la même maladie,
 *   sur une autre capacité. Non traité ici faute de symptôme observé, et parce
 *   que la copie de l'edge function tourne dans un autre runtime (Deno) : la
 *   rapatrier suppose de résoudre le partage de code entre le bundle et les
 *   fonctions, ce qui est un chantier distinct.
 */

import type { EmployeeRole } from '../types';

/**
 * Rôles dont les heures doivent être ventilées par type de travail
 * (étude / suivi / mise en service / chantier).
 *
 * À ne pas confondre avec BE_35H_ROLES : un apprenti ventile comme un BE mais
 * reste au seuil d'heures commun.
 */
export const BE_ROLES: readonly EmployeeRole[] = ['bureau_etude', 'apprenti_be'] as const;

/**
 * Rôles dont le temps de travail hebdomadaire de référence est 35 h.
 * L'apprenti en est exclu : il suit le seuil des autres salariés.
 */
export const BE_35H_ROLES: readonly EmployeeRole[] = ['bureau_etude'] as const;

/**
 * Rôles de gestion : ils agissent sur les données de tout le monde, pas
 * seulement sur les leurs (CRUD projets, rentabilité, matériel, ventilations
 * d'autrui). C'est ce qui distingue bureau_etude d'apprenti_be alors que les
 * deux ventilent : le premier peut le faire pour les autres, le second non.
 */
export const MANAGEMENT_ROLES: readonly EmployeeRole[] = ['admin', 'bureau_etude'] as const;

/** Ce rôle doit-il ventiler ses heures par type de travail ? */
export function isBERole(role: string | null | undefined): boolean {
  return !!role && (BE_ROLES as readonly string[]).includes(role);
}

/** Ce rôle agit-il sur les données des autres salariés ? */
export function isManagementRole(role: string | null | undefined): boolean {
  return !!role && (MANAGEMENT_ROLES as readonly string[]).includes(role);
}

/** Ce rôle est-il au forfait 35 h ? */
export function isBE35hRole(role: string | null | undefined): boolean {
  return !!role && (BE_35H_ROLES as readonly string[]).includes(role);
}
